import { useState, useMemo } from "react";

// Constantes normativas 2026 (Perú) — D.L. 1258 y D.L. 1381
const UIT_2026 = 5500;
const DEDUCCION_TRABAJO_UIT = 7; 
const TOPE_GASTOS_ADICIONALES_UIT = 3; 
const TOPE_DEDUCCION_4TA_UIT = 24;

const TRAMOS_RENTA = [
  { limite: 5 * UIT_2026, tasa: 0.08 },
  { limite: 20 * UIT_2026, tasa: 0.14 },
  { limite: 35 * UIT_2026, tasa: 0.17 },
  { limite: 45 * UIT_2026, tasa: 0.20 },
  { limite: Infinity, tasa: 0.30 },
];

const CONCEPTOS_GASTO = [
  { id: "alquiler", etiqueta: "Alquiler de vivienda", porcentaje: 0.30, ayuda: "30% del monto pagado en el año" },
  { id: "hipoteca", etiqueta: "Intereses de crédito hipotecario (1ra vivienda)", porcentaje: 1.00, ayuda: "100% de los intereses pagados" },
  { id: "salud", etiqueta: "Honorarios médicos y odontológicos", porcentaje: 0.30, ayuda: "30% del monto pagado (tuyo, cónyuge e hijos)" },
  { id: "profesionales", etiqueta: "Otros servicios profesionales (4ta categoría)", porcentaje: 0.30, ayuda: "30% del monto pagado con recibo por honorarios" },
  { id: "restaurantes", etiqueta: "Restaurantes, bares y hoteles", porcentaje: 0.15, ayuda: "15% del monto consumido" },
  { id: "trabajadoraHogar", etiqueta: "EsSalud de trabajadora del hogar", porcentaje: 1.00, ayuda: "100% de las aportaciones pagadas" },
];

function calcularImpuestoAnual(ingresoAnualAfecto) {
  let restante = Math.max(0, ingresoAnualAfecto);
  let anterior = 0;
  let impuesto = 0;
  for (const tramo of TRAMOS_RENTA) {
    if (restante <= 0) break;
    const anchoTramo = tramo.limite - anterior;
    const montoEnTramo = Math.min(restante, anchoTramo);
    impuesto += montoEnTramo * tramo.tasa;
    restante -= montoEnTramo;
    anterior = tramo.limite;
  }
  return impuesto;
}

function formatearSoles(valor) {
  return valor.toLocaleString("es-PE", {
    style: "currency",
    currency: "PEN",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function limpiarNumero(texto) {
  const limpio = texto.replace(/[^0-9.]/g, "");
  const primerPunto = limpio.indexOf(".");
  if (primerPunto === -1) return limpio;
  return limpio.slice(0, primerPunto + 1) + limpio.slice(primerPunto + 1).replace(/\./g, "");
}

export default function CalculadoraDevolucionSunat() {
  const [ingreso5ta, setIngreso5ta] = useState(0);
  const [retencion5ta, setRetencion5ta] = useState(0);
  const [ingreso4ta, setIngreso4ta] = useState(15000);
  const [retencion4ta, setRetencion4ta] = useState(1200);

  const [gastos, setGastos] = useState({
    alquiler: 0,
    hipoteca: 0,
    salud: 0,
    profesionales: 0,
    restaurantes: 0,
    trabajadoraHogar: 0,
  });

  const actualizarGasto = (id, valor) => {
    setGastos((prev) => ({ ...prev, [id]: valor }));
  };

  const resultado = useMemo(() => {
    const bruto5ta = Math.max(0, Number(ingreso5ta) || 0);
    const bruto4ta = Math.max(0, Number(ingreso4ta) || 0);
    const retenido5ta = Math.max(0, Number(retencion5ta) || 0);
    const retenido4ta = Math.max(0, Number(retencion4ta) || 0);
    const retenido = retenido5ta + retenido4ta;

    const topeDeduccion4ta = TOPE_DEDUCCION_4TA_UIT * UIT_2026;
    const deduccion4ta = Math.min(bruto4ta * 0.20, topeDeduccion4ta);
    const rentaNeta4ta = bruto4ta - deduccion4ta;

    const detalleGastos = CONCEPTOS_GASTO.map((c) => {
      const monto = Math.max(0, Number(gastos[c.id]) || 0);
      const deducible = monto * c.porcentaje;
      return { ...c, monto, deducible };
    });
    const totalDeducibleGastos = detalleGastos.reduce((acc, c) => acc + c.deducible, 0);
    const topeGastos = TOPE_GASTOS_ADICIONALES_UIT * UIT_2026;
    const deduccionGastosAplicada = Math.min(totalDeducibleGastos, topeGastos);
    const seAplicoTopeGastos = totalDeducibleGastos > topeGastos;

    const rentaNetaTrabajo = bruto5ta + rentaNeta4ta;
    const deduccionFija7Uit = DEDUCCION_TRABAJO_UIT * UIT_2026;
    const rentaImponibleSinPiso = rentaNetaTrabajo - deduccionFija7Uit - deduccionGastosAplicada;
    const rentaImponible = Math.max(0, rentaImponibleSinPiso);

    const impuestoAnual = calcularImpuestoAnual(rentaImponible);
    const saldo = retenido - impuestoAnual; 

    return {
      bruto5ta,
      bruto4ta,
      retenido5ta,
      retenido4ta,
      deduccion4ta,
      rentaNeta4ta,
      detalleGastos,
      totalDeducibleGastos,
      deduccionGastosAplicada,
      seAplicoTopeGastos,
      rentaNetaTrabajo,
      deduccionFija7Uit,
      rentaImponible,
      impuestoAnual,
      retenido,
      saldo,
    };
  }, [ingreso5ta, ingreso4ta, retencion5ta, retencion4ta, gastos]);

  const inputClase =
    "w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-slate-900 font-mono focus:outline-none focus:ring-2 focus:ring-red-700/30 focus:border-red-700";

  return (
    <div className="w-full min-h-full px-4 py-10 bg-stone-50">
      <div className="max-w-xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="flex items-center justify-center text-lg text-white bg-red-700 rounded-full h-11 w-11">
            🏢
          </div>
          <div>
            <h1 className="text-xl font-bold tracking-tight text-slate-900">
              Calculadora de Devolución SUNAT
            </h1>
            <p className="text-sm text-slate-500">Perú 2026 · Renta de trabajo y gastos deducibles (hasta 3 UIT)</p>
          </div>
        </div>

        <div className="overflow-hidden bg-white border shadow-sm rounded-2xl border-slate-200">
          <div className="p-6 space-y-5 border-b border-slate-100">
            <div>
              <p className="mb-3 text-xs font-semibold tracking-wide uppercase text-slate-500">
                Ingresos Independientes (4ta Categoría)
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Ingresos Brutos Anuales
                  </label>
                  <div className="relative">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 font-mono text-sm">S/</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={ingreso4ta}
                      onChange={(e) => setIngreso4ta(limpiarNumero(e.target.value))}
                      className={`${inputClase} pl-9`}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Retenciones del 8%
                  </label>
                  <div className="relative">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 font-mono text-sm">S/</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={retencion4ta}
                      onChange={(e) => setRetencion4ta(limpiarNumero(e.target.value))}
                      className={`${inputClase} pl-9`}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-slate-100">
              <p className="mb-3 text-xs font-semibold tracking-wide uppercase text-slate-500">
                Ingresos en Planilla (5ta Categoría)
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Ingresos Brutos Anuales
                  </label>
                  <div className="relative">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 font-mono text-sm">S/</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={ingreso5ta}
                      onChange={(e) => setIngreso5ta(limpiarNumero(e.target.value))}
                      className={`${inputClase} pl-9`}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Retenciones 5ta Cat.
                  </label>
                  <div className="relative">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 font-mono text-sm">S/</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={retencion5ta}
                      onChange={(e) => setRetencion5ta(limpiarNumero(e.target.value))}
                      className={`${inputClase} pl-9`}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-slate-100">
              <p className="mb-1 text-xs font-semibold tracking-wide uppercase text-slate-500">
                Gastos Deducibles (Adicionales a las 7 UIT)
              </p>
              <p className="mb-3 text-xs text-slate-400">
                Solo cuenta lo pagado con medios bancarizados y con comprobante electrónico. Tope
                conjunto: 3 UIT ({formatearSoles(TOPE_GASTOS_ADICIONALES_UIT * UIT_2026)}).
              </p>
              <div className="space-y-3">
                {CONCEPTOS_GASTO.map((c) => (
                  <div key={c.id}>
                    <label className="block mb-1 text-sm font-medium text-slate-700">{c.etiqueta}</label>
                    <div className="relative">
                      <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 font-mono text-sm">S/</span>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={gastos[c.id]}
                        onChange={(e) => actualizarGasto(c.id, limpiarNumero(e.target.value))}
                        className={`${inputClase} pl-9`}
                      />
                    </div>
                    <p className="mt-0.5 text-xs text-slate-400">{c.ayuda}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="p-6 bg-slate-900">
            <p className="mb-1 text-xs tracking-wide uppercase text-slate-400">
              {resultado.saldo >= 0 ? "¡Tienes saldo a favor! · Monto proyectado a devolver" : "Impuesto por pagar estimado"}
            </p>
            <p className="mb-5 font-mono text-4xl font-bold text-white">
              {formatearSoles(Math.abs(resultado.saldo))}
            </p>

            <p className="mb-2 text-xs font-semibold tracking-wide uppercase text-slate-400">
              Desglose de la Declaración Jurada Anual
            </p>
            <div className="space-y-1.5 text-sm">
              {resultado.bruto4ta > 0 && (
                <>
                  <div className="flex justify-between text-slate-300">
                    <span>Ingresos Brutos 4ta</span>
                    <span className="font-mono">{formatearSoles(resultado.bruto4ta)}</span>
                  </div>
                  <div className="flex justify-between text-slate-300">
                    <span>(-) Deducción 20% (Max 24 UIT)</span>
                    <span className="font-mono">−{formatearSoles(resultado.deduccion4ta)}</span>
                  </div>
                </>
              )}
              {resultado.bruto5ta > 0 && (
                <div className="flex justify-between text-slate-300">
                  <span>Ingresos Brutos 5ta</span>
                  <span className="font-mono">{formatearSoles(resultado.bruto5ta)}</span>
                </div>
              )}
              <div className="flex justify-between text-slate-300 border-t border-slate-700 pt-1.5 mt-1.5">
                <span>Renta Neta de Trabajo</span>
                <span className="font-mono">{formatearSoles(resultado.rentaNetaTrabajo)}</span>
              </div>
              <div className="flex justify-between text-slate-300">
                <span>(-) Deducción Fija 7 UIT</span>
                <span className="font-mono">−{formatearSoles(resultado.deduccionFija7Uit)}</span>
              </div>
              <div className="flex justify-between text-slate-300">
                <span>(-) Gastos Deducibles Sustentados</span>
                <span className="font-mono">−{formatearSoles(resultado.deduccionGastosAplicada)}</span>
              </div>
              {resultado.seAplicoTopeGastos && (
                <p className="text-xs text-amber-400">
                  Tope de 3 UIT aplicado (tus gastos deducibles suman{" "}
                  {formatearSoles(resultado.totalDeducibleGastos)})
                </p>
              )}
              <div className="flex justify-between text-white font-medium border-t border-slate-700 pt-1.5 mt-1.5">
                <span>Renta Imponible (Base de cálculo)</span>
                <span className="font-mono">{formatearSoles(resultado.rentaImponible)}</span>
              </div>
              <div className="flex justify-between font-medium text-white">
                <span>Impuesto a la Renta Calculado</span>
                <span className="font-mono">{formatearSoles(resultado.impuestoAnual)}</span>
              </div>
              <div className="flex justify-between text-slate-300">
                <span>(-) Pagos a cuenta y Retenciones (4ta y 5ta)</span>
                <span className="font-mono">−{formatearSoles(resultado.retenido)}</span>
              </div>
            </div>

            <div className="pt-4 mt-4 border-t border-slate-700">
              <p className="mb-1 text-xs font-semibold text-slate-300">¿Por qué tengo este saldo?</p>
              <p className="text-xs leading-relaxed text-slate-400">
                El saldo a favor aparece cuando lo que ya te retuvieron en el año (tu empleador o
                tus clientes) supera el impuesto que realmente te corresponde pagar. Esto pasa
                sobre todo cuando registras gastos deducibles adicionales (alquiler, salud,
                restaurantes) o cuando tuviste meses sin ingresos en planilla.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-4 space-y-1 text-xs leading-relaxed text-slate-400">
          <p>
            Esta es una herramienta referencial basada en la UIT 2026 ({formatearSoles(UIT_2026)}). El
            resultado final depende de la información consolidada y validada por SUNAT en el
            Formulario Virtual 709.
          </p>
          <p>
            Los gastos solo son deducibles si están sustentados en comprobantes de pago
            electrónicos y, cuando corresponda, pagados con medios de pago bancarizados. Los
            porcentajes deducibles y el tope de 3 UIT están sujetos a cambios normativos. No
            constituye asesoría tributaria.
          </p>
        </div>
      </div>
    </div>
  );
}